import { Client, type Room } from '@colyseus/sdk';
import { colyseusHttpUrl } from '../net/colyseusUrl';
import {
  RUN_MODE_STORAGE_KEY,
  RUN_CONTRACT_STORAGE_KEY,
  RUN_SEED_STORAGE_KEY,
  allRunContracts,
  type RunContractKind,
} from '../game/runContracts';
import { getDailyChallenge } from '../game/dailyChallenge';
import {
  DRIVER_LOADOUT_STORAGE_KEY,
  allDriverLoadouts,
  currentDriverRank,
  isDriverLoadoutUnlocked,
  type DriverLoadoutId,
} from '../game/driverLoadouts';

type LobbyCallbacks = {
  onQuickPlay: () => void;
  onGameStart: (info: {
    roomCode: string;
    seed: number;
    role: 'driver' | 'cop';
    sessionId: string;
    room: Room;
  }) => void;
};

let sharedClient: Client | null = null;

function getClient(): Client {
  if (!sharedClient) sharedClient = new Client(colyseusHttpUrl());
  return sharedClient;
}

/**
 * prd2 Day 18 — room codes, ready toggles, start when both ready.
 * Colyseus: lobby state + messages for ready / in-game relay.
 */
export function initLobby(options: LobbyCallbacks): void {
  const lobbyEl = document.getElementById('lobby-screen');
  if (!lobbyEl) return;
  const lobbyScreen: HTMLElement = lobbyEl;

  const nameInput = document.getElementById('lobby-name') as HTMLInputElement | null;
  const quickBtn = document.getElementById('lobby-quick-play');
  const dailyBtn = document.getElementById('lobby-daily-challenge');
  const dailyTitleEl = document.getElementById('lobby-daily-title');
  const dailyMetaEl = document.getElementById('lobby-daily-meta');
  const jobBoardEl = document.getElementById('lobby-job-board');
  const loadoutBoardEl = document.getElementById('lobby-loadout-board');
  const createBtn = document.getElementById('lobby-create-room');
  const joinBtn = document.getElementById('lobby-join-room');
  const joinCodeInput = document.getElementById(
    'lobby-join-code',
  ) as HTMLInputElement | null;
  const codeDisplay = document.getElementById('lobby-room-code');
  const copyBtn = document.getElementById('lobby-copy-code');
  const readyBtn = document.getElementById('lobby-ready') as HTMLButtonElement | null;
  const statusEl = document.getElementById('lobby-status');
  const playersEl = document.getElementById('lobby-players');

  let room: Room | null = null;
  let ready = false;
  let inRoom = false;
  let gameStarted = false;
  let connecting = false;
  let selectedContract: RunContractKind = 'escape';
  let selectedLoadout: DriverLoadoutId = 'balanced';

  function setStatus(text: string) {
    if (statusEl) statusEl.textContent = text;
  }

  function displayName(): string {
    const s = nameInput?.value.trim().slice(0, 24);
    return s && s.length > 0 ? s : 'Player';
  }

  function syncLobbyUi() {
    if (!room || lobbyScreen.hidden) return;
    const s = room.state as {
      roomCode?: string;
      phase?: string;
      players?: Map<
        string,
        { name: string; ready: boolean; isHost: boolean }
      >;
    };
    const code = s.roomCode ?? '';
    if (codeDisplay) codeDisplay.textContent = code;
    if (codeDisplay?.parentElement && code) {
      codeDisplay.parentElement.hidden = false;
    }

    if (!playersEl || !s.players) return;
    const list: { name: string; ready: boolean; isHost: boolean }[] = [];
    s.players.forEach((p) => {
      list.push({
        name: p.name,
        ready: p.ready,
        isHost: p.isHost,
      });
    });
    list.sort((a, b) => (a.isHost === b.isHost ? 0 : a.isHost ? -1 : 1));

    playersEl.innerHTML = '';
    for (const p of list) {
      const li = document.createElement('li');
      li.className = 'lobby-player';
      const tag = p.isHost ? 'Host' : 'Guest';
      const r = p.ready ? 'ready' : 'not ready';
      li.textContent = `${p.name} · ${tag} · ${r}`;
      playersEl.appendChild(li);
    }

    const n = list.length;
    const allReady = n === 2 && list.every((x) => x.ready);
    if (n < 2) {
      setStatus('Share the room code with a friend.');
    } else if (!allReady) {
      setStatus('Both players tap Ready when set.');
    } else {
      setStatus('Starting…');
    }
  }

  function tryStartGameFromState() {
    if (!room || gameStarted) return;
    const s = room.state as { phase?: string; seed?: number; sessionId?: string; roomCode?: string; players?: Map<string, { role?: string }> };
    if (s.phase !== 'playing') return;
    const me = s.players?.get(room.sessionId);
    const rr = me?.role;
    const role: 'driver' | 'cop' =
      rr === 'driver' || rr === 'cop' ? rr : 'driver';
    gameStarted = true;
    document.body.classList.remove('lobby-active');
    lobbyScreen.hidden = true;
    options.onGameStart({
      roomCode: s.roomCode ?? '',
      seed: Number(s.seed) >>> 0,
      role,
      sessionId: s.sessionId ?? '',
      room,
    });
  }

  function bindRoom(r: Room) {
    room = r;
    inRoom = true;
    gameStarted = false;
    ready = false;
    updateReadyButton();

    syncLobbyUi();
    r.onStateChange(() => {
      syncLobbyUi();
      tryStartGameFromState();
    });
    tryStartGameFromState();

    r.onLeave(() => {
      if (lobbyScreen.hidden) return;
      setStatus('Disconnected from lobby.');
      room = null;
      inRoom = false;
      ready = false;
      updateReadyButton();
    });
  }

  function updateReadyButton() {
    if (!readyBtn) return;
    readyBtn.textContent = ready ? 'Cancel ready' : 'Ready';
    readyBtn.dataset.ready = ready ? 'true' : 'false';
    const open = room != null;
    readyBtn.disabled = !inRoom || !open;
  }

  function setConnecting(next: boolean) {
    connecting = next;
    if (createBtn instanceof HTMLButtonElement) createBtn.disabled = connecting;
    if (joinBtn instanceof HTMLButtonElement) joinBtn.disabled = connecting;
    if (quickBtn instanceof HTMLButtonElement) quickBtn.disabled = connecting;
    if (dailyBtn instanceof HTMLButtonElement) dailyBtn.disabled = connecting;
    if (joinCodeInput) joinCodeInput.disabled = connecting;
  }

  function syncJobBoard(): void {
    if (!jobBoardEl) return;
    const cards = jobBoardEl.querySelectorAll<HTMLButtonElement>('[data-contract-kind]');
    cards.forEach((card) => {
      card.dataset.selected =
        card.dataset.contractKind === selectedContract ? 'true' : 'false';
    });
  }

  function syncLoadoutBoard(): void {
    if (!loadoutBoardEl) return;
    const cards = loadoutBoardEl.querySelectorAll<HTMLButtonElement>('[data-loadout-id]');
    cards.forEach((card) => {
      card.dataset.selected = card.dataset.loadoutId === selectedLoadout ? 'true' : 'false';
    });
  }

  if (jobBoardEl) {
    jobBoardEl.innerHTML = '';
    for (const contract of allRunContracts()) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'job-card';
      card.dataset.contractKind = contract.kind;
      card.innerHTML = `
        <span class="job-card__top">
          <span class="job-card__title"></span>
          <span class="job-card__difficulty"></span>
        </span>
        <span class="job-card__brief"></span>
        <span class="job-card__modifiers"></span>
        <span class="job-card__reward"></span>
      `;
      card.querySelector('.job-card__title')!.textContent = contract.title;
      card.querySelector('.job-card__difficulty')!.textContent = contract.difficulty;
      card.querySelector('.job-card__brief')!.textContent = contract.intro;
      card.querySelector('.job-card__modifiers')!.textContent =
        contract.modifiers.map((m) => m.label).join(' · ');
      card.querySelector('.job-card__reward')!.textContent = contract.rewardTag;
      card.addEventListener('click', () => {
        selectedContract = contract.kind;
        syncJobBoard();
      });
      jobBoardEl.appendChild(card);
    }
    syncJobBoard();
  }

  if (loadoutBoardEl) {
    const rank = currentDriverRank();
    loadoutBoardEl.innerHTML = '';
    for (const loadout of allDriverLoadouts()) {
      const unlocked = isDriverLoadoutUnlocked(loadout, rank);
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'loadout-card';
      card.dataset.loadoutId = loadout.id;
      card.disabled = !unlocked;
      card.innerHTML = `
        <span class="loadout-card__swatch"></span>
        <span class="loadout-card__body">
          <span class="loadout-card__top">
            <span class="loadout-card__title"></span>
            <span class="loadout-card__rank"></span>
          </span>
          <span class="loadout-card__tagline"></span>
          <span class="loadout-card__perk"></span>
        </span>
      `;
      const swatch = card.querySelector<HTMLElement>('.loadout-card__swatch');
      if (swatch) {
        swatch.style.background = `linear-gradient(135deg, #${loadout.paintColor.toString(16).padStart(6, '0')}, #${loadout.accentColor.toString(16).padStart(6, '0')})`;
      }
      card.querySelector('.loadout-card__title')!.textContent = loadout.name;
      card.querySelector('.loadout-card__rank')!.textContent = unlocked
        ? 'Unlocked'
        : `Rank ${loadout.unlockRank}`;
      card.querySelector('.loadout-card__tagline')!.textContent = loadout.tagline;
      card.querySelector('.loadout-card__perk')!.textContent = loadout.perk;
      card.addEventListener('click', () => {
        if (!unlocked) return;
        selectedLoadout = loadout.id;
        syncLoadoutBoard();
      });
      loadoutBoardEl.appendChild(card);
    }
    syncLoadoutBoard();
  }

  const daily = getDailyChallenge();
  if (dailyTitleEl) dailyTitleEl.textContent = daily.title;
  if (dailyMetaEl) dailyMetaEl.textContent = daily.subtitle;

  function launchSinglePlayer(kind: RunContractKind, dailySeed?: number): void {
    try {
      sessionStorage.setItem(RUN_CONTRACT_STORAGE_KEY, kind);
      sessionStorage.setItem(DRIVER_LOADOUT_STORAGE_KEY, selectedLoadout);
      if (dailySeed != null) {
        sessionStorage.setItem(RUN_SEED_STORAGE_KEY, String(dailySeed >>> 0));
        sessionStorage.setItem(RUN_MODE_STORAGE_KEY, 'daily');
      } else {
        sessionStorage.removeItem(RUN_SEED_STORAGE_KEY);
        sessionStorage.removeItem(RUN_MODE_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
    document.body.classList.remove('lobby-active');
    lobbyScreen.hidden = true;
    options.onQuickPlay();
  }

  quickBtn?.addEventListener('click', () => {
    launchSinglePlayer(selectedContract);
  });

  dailyBtn?.addEventListener('click', () => {
    selectedContract = daily.contractKind;
    syncJobBoard();
    launchSinglePlayer(daily.contractKind, daily.seed);
  });

  createBtn?.addEventListener('click', () => {
    if (connecting || inRoom) return;
    void (async () => {
      setConnecting(true);
      setStatus('Connecting…');
      try {
        const r = await getClient().create('last_mile', {
          name: displayName(),
        });
        setStatus('Connected — share your room code.');
        bindRoom(r);
      } catch {
        setStatus('Could not create room. Is the Colyseus server running?');
      } finally {
        setConnecting(false);
      }
    })();
  });

  joinBtn?.addEventListener('click', () => {
    if (connecting || inRoom) return;
    const code = joinCodeInput?.value.trim().toUpperCase() ?? '';
    if (code.length !== 6) {
      setStatus('Enter a 6-character room code.');
      return;
    }
    void (async () => {
      setConnecting(true);
      setStatus('Looking up room…');
      const base = colyseusHttpUrl().replace(/\/$/, '');
      let roomId: string;
      try {
        const res = await fetch(
          `${base}/match/room-id?code=${encodeURIComponent(code)}`,
        );
        if (!res.ok) {
          setStatus('Room not found or server unreachable.');
          return;
        }
        const data = (await res.json()) as { roomId?: string };
        if (typeof data.roomId !== 'string') {
          setStatus('Invalid response from server.');
          return;
        }
        roomId = data.roomId;
      } catch {
        setStatus('Could not reach server. Run: npm run server:lobby');
        setConnecting(false);
        return;
      }
      setStatus('Joining…');
      try {
        const r = await getClient().joinById(roomId, {
          name: displayName(),
        });
        setStatus('Connected.');
        bindRoom(r);
      } catch {
        setStatus('Could not join (full or closed).');
      } finally {
        setConnecting(false);
      }
    })();
  });

  readyBtn?.addEventListener('click', () => {
    if (!inRoom || !room) return;
    ready = !ready;
    try {
      room.send('set_ready', { ready });
    } catch {
      /* ignore */
    }
    updateReadyButton();
  });

  copyBtn?.addEventListener('click', async () => {
    const code = codeDisplay?.textContent?.trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setStatus('Code copied to clipboard.');
    } catch {
      setStatus(`Room code: ${code}`);
    }
  });

  document.body.classList.add('lobby-active');
  setStatus('Create or join a room — server: npm run server:lobby');
}
